import { useState, useEffect, useMemo, useCallback } from 'react';
import 'maplibre-gl/dist/maplibre-gl.css';
import DeckGL from 'deck.gl';
import { Map as MapaBase } from 'react-map-gl/maplibre';
import { cellToLatLng, latLngToCell } from 'h3-js';

import { cargarDatosBase, cargarDatosOpcionales } from './datos';
import { useReloj } from './reloj';
import { ALPHAS, ETIQUETA_ALPHA as ETIQUETA_TOOLTIP } from './alfas';
import { construyeCapaGrilla } from './capas/grilla';
import { calculaAreaEscaneo, construyeCapaAreaEscaneo } from './capas/areaEscaneo';
import { preparaOnda, construyeCapaOnda } from './capas/onda';
import { construyeCapaDestinos, construyeCapaDestinosBase } from './capas/destinos';
import { construyeCapaArcos } from './capas/arcos';
import { preparaEcos, construyeCapaSegundoOrden } from './capas/segundoOrden';
import {
  construyeCapaDestinosSegundoOrden,
  construyeCapaGlowSegundoOrden,
} from './capas/destinosSegundoOrden';
import { construyeCapaArcosSegundoOrden } from './capas/arcosSegundoOrden';
import { construyeCapaEtiquetas } from './capas/etiquetas';
import Panel from './hud/Panel';
import Leyenda from './hud/Leyenda';
import Modal from './hud/Modal';
import MarkdownMini, { tituloMarkdown } from './hud/MarkdownMini';
import { EXPLICACIONES } from './hud/explicaciones';
import { clasificaTipologia } from './tipologia';
import { txt } from './i18n';

// Cuantos destinos de trabajo (de los ya ordenados por peso en
// casa_trabajo.json) reciben busqueda propia de servicios. Mas alla de esto
// solo se ve el eco de ignicion (capas/segundoOrden.js): buscar amenities
// para los 30 principales saturaria el mapa de arcos.
const TOP_K_TRABAJO_AMENITIES = 6;

// El basemap va sin etiquetas: los toponimos los pone capas/etiquetas.js por
// encima de los hexagonos, que es donde se leen
const ESTILO_MAPA =
  'https://basemaps.cartocdn.com/gl/dark-matter-nolabels-gl-style/style.json';

const VISTA_INICIAL = {
  longitude: -70.65,
  latitude: -33.47,
  zoom: 10.3,
  pitch: 0,
  bearing: 0,
};


const DORADO = '#f2b33d';
const DORADO_BRILLANTE = '#ffd54a';
const DORADO_TENUE = 'rgba(242, 179, 61, 0.28)';
const FONT_SCIFI = '"Space Grotesk", ui-monospace, "SF Mono", "Cascadia Code", monospace';

const ESTILO_MENSAJE_CENTRADO = {
  position: 'fixed',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  alignItems: 'center',
  justifyContent: 'center',
  background: '#04060a',
  color: DORADO_BRILLANTE,
  fontFamily: FONT_SCIFI,
  letterSpacing: '0.08em',
  fontSize: '0.9rem',
  textShadow: `0 0 8px ${DORADO_TENUE}`,
};

// Marca del proyecto (arriba a la izquierda), como en scl-vector-fields: el
// nombre de la app y el proyecto que la financia.
const ESTILO_MARCA = {
  position: 'fixed',
  top: 16,
  left: 16,
  zIndex: 20,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: 2,
  background: 'rgba(6, 4, 4, 0.87)',
  border: `1px solid ${DORADO_TENUE}`,
  padding: '8px 12px',
  backdropFilter: 'blur(3px)',
  fontFamily: FONT_SCIFI,
  cursor: 'pointer',
  textAlign: 'left',
};

const ESTILO_MARCA_TITULO = {
  fontSize: '0.78rem',
  fontWeight: 700,
  letterSpacing: '0.09em',
  color: DORADO_BRILLANTE,
};

const ESTILO_MARCA_PROYECTO = {
  fontSize: '0.66rem',
  letterSpacing: '0.06em',
  color: DORADO,
  opacity: 0.85,
};


// Cruza los pares [idx, minutos] de ttm.json con amenities.json y filtra los
// que ya se alcanzaron al tiempo t, ordenados de mas cercano a mas lejano.
// Incluye lon/lat porque el resultado tambien alimenta la capa de destinos
// encendidos (capas/destinos.js).
function calculaAlcanzados(alcanzables, amenitiesPorIdx, t) {
  return alcanzables
    .filter(([, minutos]) => minutos <= t)
    .map(([idx, minutos]) => {
      const amenity = amenitiesPorIdx.get(idx);
      return amenity
        ? {
            idx,
            tipo: amenity.tipo,
            name: amenity.name,
            lon: amenity.lon,
            lat: amenity.lat,
            minutos,
          }
        : null;
    })
    .filter((servicio) => servicio !== null)
    .sort((a, b) => a.minutos - b.minutos);
}

// Layout movil: bajo este ancho el panel pasa a hoja inferior, la leyenda se
// compacta y el HUD muestra solo lo esencial.
function useEsMovil() {
  const [esMovil, setEsMovil] = useState(
    () => window.matchMedia('(max-width: 700px)').matches
  );
  useEffect(() => {
    const consulta = window.matchMedia('(max-width: 700px)');
    const alCambiar = (e) => setEsMovil(e.matches);
    consulta.addEventListener('change', alCambiar);
    return () => consulta.removeEventListener('change', alCambiar);
  }, []);
  return esMovil;
}

// Paso en grados para la navegacion con flechas (~350 m, la distancia entre
// centros de celdas H3-9 vecinas).
const PASO_TECLADO = {
  ArrowUp: [0.0032, 0],
  ArrowDown: [-0.0032, 0],
  ArrowLeft: [0, -0.0040],
  ArrowRight: [0, 0.0040],
};

export default function App() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [alpha, setAlpha] = useState('050');
  const [origenH3, setOrigenH3] = useState(null);
  const [escaneando, setEscaneando] = useState(false);
  // Variable que colorea la grilla base: accesibilidad, poblacion o trabajadores.
  const [modoGrilla, setModoGrilla] = useState('accesibilidad');
  // Texto de la pantalla "Acerca de", cargado de public/acerca-de.md al abrirla.
  const [textoAcerca, setTextoAcerca] = useState(null);
  // Popup abierto (clave de EXPLICACIONES). El briefing de entrada se muestra
  // una vez por navegador; el boton ? de la leyenda lo vuelve a abrir.
  const [modal, setModal] = useState(() => {
    try {
      return localStorage.getItem('hex-briefing-visto') ? null : 'intro';
    } catch {
      return 'intro';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('hex-briefing-visto', '1');
    } catch {
      // localStorage no disponible (modo privado): el briefing se mostrara
      // en cada carga, sin efecto sobre el resto.
    }
  }, []);

  // Carga en dos etapas: primero los archivos base (la app ya es usable),
  // despues los opcionales (segundo orden e isocronas reales), que pesan la
  // mitad del total y no deben bloquear el primer render.
  useEffect(() => {
    let vivo = true;
    cargarDatosBase()
      .then((base) => {
        if (!vivo) return null;
        setDatos(base);
        return cargarDatosOpcionales();
      })
      .then((opcionales) => {
        if (vivo && opcionales) {
          setDatos((previos) => (previos ? { ...previos, ...opcionales } : previos));
        }
      })
      .catch((err) => {
        if (vivo) setError(err);
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Carga perezosa del texto "Acerca de" (public/acerca-de.md), editable sin
  // recompilar, tambien en el sitio ya publicado.
  useEffect(() => {
    if (modal !== 'acerca' || textoAcerca !== null) return;
    fetch(`${process.env.PUBLIC_URL}/${txt('app.acercaMd')}`)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error(r.status))))
      .then(setTextoAcerca)
      .catch(() => setTextoAcerca(`# ${txt('leyenda.acerca')}\n\n${txt('app.acercaError')}`));
  }, [modal, textoAcerca]);

  const esMovil = useEsMovil();
  // En pantallas tactiles el "hover" se dispara con cada toque y el tooltip
  // queda pegado sobre el mapa: se desactiva.
  const [punteroGrueso] = useState(
    () => window.matchMedia('(pointer: coarse)').matches
  );

  // Animacion del escaneo: parte de la preferencia del navegador y el boton de
  // la leyenda la cambia; la eleccion queda guardada en el navegador.
  const [sinMovimiento, setSinMovimiento] = useState(() => {
    try {
      const guardado = localStorage.getItem('hex-sin-movimiento');
      if (guardado !== null) return guardado === '1';
    } catch {
      // sin localStorage manda la preferencia del navegador
    }
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  });
  // la clase apaga tambien las animaciones CSS del HUD (estilos.css)
  useEffect(() => {
    document.documentElement.classList.toggle('sin-movimiento', sinMovimiento);
  }, [sinMovimiento]);
  const cambiaMovimiento = useCallback(() => {
    const nuevo = !sinMovimiento;
    try {
      localStorage.setItem('hex-sin-movimiento', nuevo ? '1' : '0');
    } catch {
      // la eleccion dura solo esta visita
    }
    setSinMovimiento(nuevo);
  }, [sinMovimiento]);

  const t = useReloj(escaneando, sinMovimiento);
  // Version cuantizada del reloj (pasos de 0.5 min, ~10 actualizaciones por
  // segundo) para todo lo que no necesita 60 fps: la lista de alcanzados, las
  // estadisticas y el HUD. Las capas del mapa siguen usando t continuo.
  const tPanel = Math.min(30, Math.ceil(t * 2) / 2);

  // Reinicia el escaneo aunque se clickee la celda ya seleccionada: apagar
  // primero fuerza a useReloj a resetear t a 0 antes de volver a encenderlo.
  const manejaClickCelda = useCallback((h3) => {
    setOrigenH3(h3);
    setEscaneando(false);
    requestAnimationFrame(() => setEscaneando(true));
  }, []);

  // Maximos robustos (percentil 98) para normalizar las rampas de poblacion y
  // trabajadores: unas pocas celdas extremas no lavan el resto de la escala.
  const maxima = useMemo(() => {
    if (!datos) return { poblacion: 1, trabajadores: 1 };
    const percentil = (valores, q) => {
      const ordenados = valores.slice().sort((a, b) => a - b);
      return ordenados[Math.floor(q * (ordenados.length - 1))] || 1;
    };
    return {
      poblacion: percentil(datos.grilla.map((d) => d.pop_vivienda ?? 0), 0.98),
      trabajadores: percentil(datos.grilla.map((d) => d.pop_trabajo ?? 0), 0.98),
    };
  }, [datos]);

  // Celdas del area de captacion del primer orden (alcanzables en 30 min desde
  // la celda elegida), con marca de borde. Solo depende de la celda, no de t.
  const celdasArea = useMemo(
    () => (datos && origenH3 ? calculaAreaEscaneo(origenH3, datos.isocronas, datos.grilla) : null),
    [datos, origenH3]
  );

  const amenitiesPorIdx = useMemo(() => {
    if (!datos) return new Map();
    return new Map(datos.amenities.map((amenity) => [amenity.idx, amenity]));
  }, [datos]);

  const alcanzables = useMemo(() => {
    if (!datos || origenH3 === null) return [];
    return datos.ttm[origenH3] ?? [];
  }, [datos, origenH3]);

  const alcanzados = useMemo(
    () => calculaAlcanzados(alcanzables, amenitiesPorIdx, tPanel),
    [alcanzables, amenitiesPorIdx, tPanel]
  );

  // Ids de celda validos, para la navegacion con teclado.
  const celdasH3 = useMemo(
    () => (datos ? new Set(datos.grilla.map((d) => d.h3)) : new Set()),
    [datos]
  );

  // Media de la ciudad por alfa, el punto de comparacion del HUD: permite leer
  // la puntuacion de la celda contra el Gran Santiago completo.
  const mediaCiudad = useMemo(() => {
    if (!datos) return null;
    const medias = {};
    for (const { clave } of ALPHAS) {
      let suma = 0;
      for (const fila of datos.grilla) suma += fila.ratings?.[clave]?.total ?? 0;
      medias[clave] = suma / datos.grilla.length;
    }
    return medias;
  }, [datos]);

  const origenPos = useMemo(() => {
    if (origenH3 === null) return null;
    const [lat, lon] = cellToLatLng(origenH3);
    return [lon, lat];
  }, [origenH3]);

  const celda = useMemo(() => {
    if (!datos || origenH3 === null) return null;
    return datos.grilla.find((d) => d.h3 === origenH3) ?? null;
  }, [datos, origenH3]);

  // Tipologia del panel A de la figura: accesibilidad retenida (alfa 0.5) vs
  // peso de la poblacion trabajadora. Fija, no depende del selector de alfa;
  // es la clasificacion del featured graphic.
  const tipologia = useMemo(() => {
    if (!celda) return null;
    const acc50 = celda.ratings?.['050']?.total ?? 0;
    return clasificaTipologia(acc50, celda.peso);
  }, [celda]);

  // Segundo orden: donde trabajan quienes viven en la celda elegida. Puede
  // no existir si casa_trabajo.json no se genero (dependencia opcional).
  const destinosTrabajo = useMemo(() => {
    if (!datos || origenH3 === null) return null;
    return datos.casaTrabajo[origenH3] ?? null;
  }, [datos, origenH3]);

  const topDestinosTrabajo = useMemo(
    () => destinosTrabajo?.top.slice(0, TOP_K_TRABAJO_AMENITIES) ?? null,
    [destinosTrabajo]
  );

  // Segundo orden real: para cada uno de los principales destinos de
  // trabajo, que servicios se alcanzan DESDE AHI (no desde la vivienda).
  // datos.ttm cubre toda la grilla como origen (03-ttm.py usa los mismos
  // centroides para residencias y para lugares de trabajo), asi que
  // datos.ttm[work_h3] ya trae esa respuesta sin recalcular nada. Si un
  // servicio se alcanza desde varios destinos de trabajo, se queda con el
  // tiempo mas corto y el peso del destino que lo logra.
  const alcanzablesSegundoOrden = useMemo(() => {
    const combinados = new Map();
    if (!datos || !topDestinosTrabajo || topDestinosTrabajo.length === 0) return combinados;
    const pesoMaximo = topDestinosTrabajo[0][1];
    for (const [workH3, peso] of topDestinosTrabajo) {
      const pesoRelativo = peso / pesoMaximo;
      const lista = datos.ttm[workH3] ?? [];
      for (const [idx, minutos] of lista) {
        const actual = combinados.get(idx);
        if (!actual || minutos < actual.minutos) {
          combinados.set(idx, { minutos, pesoRelativo });
        }
      }
    }
    return combinados;
  }, [datos, topDestinosTrabajo]);

  // Subconjunto de amenities alcanzables desde el trabajo, para las capas de
  // segundo orden (halo y punto). Filtrarlo aqui evita recorrer los 4.659
  // amenities dos veces por frame.
  const candidatosSegundoOrden = useMemo(() => {
    if (!datos || alcanzablesSegundoOrden.size === 0) return [];
    return datos.amenities.filter((a) => alcanzablesSegundoOrden.has(a.idx));
  }, [datos, alcanzablesSegundoOrden]);

  // Estadisticas del segundo orden para el HUD: cuantos servicios ya se ven
  // desde el trabajo al tiempo t, y cuantos de esos son NUEVOS (no estaban
  // ya alcanzables desde la vivienda): la cobertura que el primer orden por
  // si solo no explica.
  const statsSegundoOrden = useMemo(() => {
    if (alcanzablesSegundoOrden.size === 0) return null;
    const idxDesdeCasa = new Set(alcanzables.map(([idx]) => idx));
    let encendidos = 0;
    let nuevos = 0;
    for (const [idx, info] of alcanzablesSegundoOrden) {
      if (info.minutos <= tPanel) {
        encendidos++;
        if (!idxDesdeCasa.has(idx)) nuevos++;
      }
    }
    return { total: alcanzablesSegundoOrden.size, encendidos, nuevos };
  }, [alcanzablesSegundoOrden, alcanzables, tPanel]);

  // Navegacion con teclado: las flechas mueven la seleccion a la celda vecina
  // (o parten del centro de la ciudad) y relanzan el escaneo. Es la
  // alternativa sin mouse al click sobre el mapa.
  useEffect(() => {
    if (!datos) return undefined;
    function alTeclear(e) {
      if (modal) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') return;
      const paso = PASO_TECLADO[e.key];
      if (!paso) return;
      e.preventDefault();
      if (origenH3 === null) {
        const centro = latLngToCell(VISTA_INICIAL.latitude, VISTA_INICIAL.longitude, 9);
        if (celdasH3.has(centro)) manejaClickCelda(centro);
        return;
      }
      const [lat, lng] = cellToLatLng(origenH3);
      // si el paso corto cae en la misma celda o fuera de la grilla, se
      // intenta un paso mas largo antes de rendirse
      for (const factor of [1, 1.7]) {
        const objetivo = latLngToCell(lat + paso[0] * factor, lng + paso[1] * factor, 9);
        if (objetivo !== origenH3 && celdasH3.has(objetivo)) {
          manejaClickCelda(objetivo);
          return;
        }
      }
    }
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [datos, modal, origenH3, celdasH3, manejaClickCelda]);

  const abreAcerca = useCallback(() => setModal('acerca'), []);

  // Click sobre un punto de servicio: se reenvia a la celda que lo contiene,
  // porque el punto tapa a la grilla en el picking de deck.gl.
  const manejaClickAmenity = useCallback(
    (info) => {
      const amenity = info?.object;
      if (!amenity) return;
      const h3 = latLngToCell(amenity.lat, amenity.lon, 9);
      if (celdasH3.has(h3)) manejaClickCelda(h3);
    },
    [celdasH3, manejaClickCelda]
  );

  // Tooltip al pasar el cursor: resumen de la celda (o del servicio) sin
  // necesidad de lanzar un escaneo.
  const obtieneTooltip = useCallback(
    ({ object, layer }) => {
      if (!object) return null;
      const estilo = {
        backgroundColor: 'rgba(6, 8, 12, 0.92)',
        border: '1px solid rgba(242, 179, 61, 0.55)',
        color: '#e8e8ec',
        fontFamily: FONT_SCIFI,
        fontSize: '0.72rem',
        letterSpacing: '0.04em',
        padding: '6px 9px',
      };
      if (layer.id === 'grilla') {
        const total = object.ratings?.[alpha]?.total ?? 0;
        const acc50 = object.ratings?.['050']?.total ?? 0;
        const tipo = clasificaTipologia(acc50, object.peso);
        return {
          html:
            `<div><strong>E2SFCA ${total.toFixed(2)}</strong> (α = ${ETIQUETA_TOOLTIP[alpha]})</div>` +
            `<div>${tipo.nombre}</div>` +
            `<div>${Math.round(object.pop_vivienda ?? 0)} ${txt('tooltip.residentes')}; ` +
            `${Math.round(object.pop_trabajo ?? 0)} ${txt('tooltip.trabajadores')}</div>`,
          style: estilo,
        };
      }
      if (layer.id === 'destinos' || layer.id === 'destinos-base') {
        const minutos =
          object.minutos !== undefined
            ? `; ${Math.round(object.minutos)} ${txt('tooltip.caminata')}`
            : '';
        return {
          html: `<div><strong>${object.name}</strong></div><div>${object.tipo}${minutos}</div>`,
          style: estilo,
        };
      }
      return null;
    },
    [alpha]
  );

  // Capas que no dependen del reloj de animacion, memoizadas para no
  // reconstruirlas (con sus closures sobre miles de elementos) en cada frame.
  const capaGrilla = useMemo(
    () =>
      datos
        ? construyeCapaGrilla({
            grilla: datos.grilla,
            alpha,
            modo: modoGrilla,
            maxima,
            origenH3,
            onClick: manejaClickCelda,
          })
        : null,
    [datos, alpha, modoGrilla, maxima, origenH3, manejaClickCelda]
  );
  const capaAreaEscaneo = useMemo(() => construyeCapaAreaEscaneo(celdasArea), [celdasArea]);

  // Celdas de la onda y de los ecos, fijas por seleccion: la animacion vive
  // en los accessors de color de las capas, que solo actualizan el buffer de
  // colores por frame (regenerar el data re-teselaba los hexagonos).
  const onda = useMemo(
    () =>
      datos
        ? preparaOnda({ origenH3, isocronas: datos.isocronas, grilla: datos.grilla })
        : null,
    [datos, origenH3]
  );
  const ecos = useMemo(
    () =>
      datos && destinosTrabajo
        ? preparaEcos({
            destinos: destinosTrabajo.top,
            isocronas: datos.isocronas,
            grilla: datos.grilla,
          })
        : null,
    [datos, destinosTrabajo]
  );
  const capaDestinosBase = useMemo(
    () =>
      datos
        ? construyeCapaDestinosBase({ amenities: datos.amenities, onClick: manejaClickAmenity })
        : null,
    [datos, manejaClickAmenity]
  );

  if (error) {
    return (
      <div style={ESTILO_MENSAJE_CENTRADO}>
        <p>
          {txt('comun.error')}: {error.message}
        </p>
      </div>
    );
  }

  if (!datos) {
    return (
      <div style={ESTILO_MENSAJE_CENTRADO}>
        <div className="hud-spinner" aria-hidden="true" />
        <p>{txt('comun.cargando')}</p>
      </div>
    );
  }

  const capaOnda = construyeCapaOnda({ onda, t });
  const capaDestinos = construyeCapaDestinos({ alcanzados, onClick: manejaClickAmenity });
  const capaArcos = construyeCapaArcos({
    origenPos,
    amenitiesPorIdx,
    alcanzables,
    t,
  });
  const capaSegundoOrden = construyeCapaSegundoOrden({ celdas: ecos, t });
  const capaArcosSegundoOrden = construyeCapaArcosSegundoOrden({
    destinosTrabajo: topDestinosTrabajo,
    ttm: datos.ttm,
    amenitiesPorIdx,
    t,
  });
  const capaGlowSegundoOrden = construyeCapaGlowSegundoOrden({
    candidatos: candidatosSegundoOrden,
    alcanzablesSegundoOrden,
    t,
  });
  const capaDestinosSegundoOrden = construyeCapaDestinosSegundoOrden({
    candidatos: candidatosSegundoOrden,
    alcanzablesSegundoOrden,
    t,
  });

  return (
    <div style={{ position: 'fixed', inset: 0 }}>
      <div style={{ position: 'absolute', inset: 0, cursor: 'crosshair' }}>
        <DeckGL
          initialViewState={VISTA_INICIAL}
          controller={true}
          useDevicePixels={esMovil ? 1 : true}
          getTooltip={punteroGrueso ? undefined : obtieneTooltip}
          layers={[
            capaGrilla,
            capaAreaEscaneo,
            capaSegundoOrden,
            capaArcosSegundoOrden,
            capaOnda,
            capaGlowSegundoOrden,
            capaDestinosSegundoOrden,
            capaDestinosBase,
            capaDestinos,
            capaArcos,
            // los toponimos van al final, sobre todas las demas capas
            construyeCapaEtiquetas(),
          ]}
        >
          <MapaBase mapStyle={ESTILO_MAPA} />
        </DeckGL>
      </div>

      {/* El badge abre "Acerca de"; en movil es su unico punto de acceso
          (la leyenda no se muestra ahi). */}
      <button
        type="button"
        style={ESTILO_MARCA}
        onClick={abreAcerca}
        aria-label={txt('app.acercaAria')}
      >
        <span style={ESTILO_MARCA_TITULO}>
          {'\u{1F426}'} {esMovil ? txt('app.marcaCorta') : txt('app.marca')}
        </span>
        {!esMovil && (
          <span style={ESTILO_MARCA_PROYECTO}>ANID FONDECYT REGULAR 1261835</span>
        )}
      </button>

      <Panel
        celda={celda}
        esMovil={esMovil}
        alpha={alpha}
        alcanzados={alcanzados}
        totalAlcanzables={alcanzables.length}
        t={tPanel}
        mediaCiudad={mediaCiudad}
        destinosTrabajo={destinosTrabajo}
        statsSegundoOrden={statsSegundoOrden}
        topDestinosTrabajo={topDestinosTrabajo}
        tipologia={tipologia}
        onInfo={setModal}
        onVerTipologia={() => setModoGrilla('tipologia')}
        mapaEnTipologia={modoGrilla === 'tipologia'}
      />

      {/* En movil la leyenda no entra junto al selector de alfa y no es
          esencial: el modo por defecto y el badge (acerca de) la cubren. */}
      {!esMovil && (
        <Leyenda
          modo={modoGrilla}
          onModo={setModoGrilla}
          alpha={alpha}
          onAlpha={setAlpha}
          maxima={maxima}
          tipologia={tipologia}
          onInfo={setModal}
          onAcerca={abreAcerca}
          sinMovimiento={sinMovimiento}
          onMovimiento={cambiaMovimiento}
        />
      )}

      {modal && EXPLICACIONES[modal] && (
        <Modal
          titulo={EXPLICACIONES[modal].titulo}
          codigo={EXPLICACIONES[modal].codigo}
          paginas={
            (esMovil && EXPLICACIONES[modal].paginasMovil) ||
            EXPLICACIONES[modal].paginas
          }
          onClose={() => setModal(null)}
        >
          {EXPLICACIONES[modal].cuerpo}
        </Modal>
      )}

      {modal === 'acerca' && (
        <Modal
          titulo={tituloMarkdown(textoAcerca)}
          codigo={txt('app.codigoAcerca')}
          onClose={() => setModal(null)}
        >
          {textoAcerca ? <MarkdownMini texto={textoAcerca} /> : <p>{txt('comun.cargandoCorto')}</p>}
        </Modal>
      )}
    </div>
  );
}
